/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change a variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Grid-selector price update
document.addEventListener("DOMContentLoaded", function () {
  const popup = document.querySelector("#product-customizer");
  if (!popup) return;

  const updatePopupPrice = () => {
    const total = popup.querySelector(".popup__total");
    const priceDisplay = total?.querySelector("span.price");
    if (!total || !priceDisplay) return;

    const basePrice = parseInt(total.dataset.main_price);
    const addonRadios = popup.querySelectorAll(
      '.grid-selector input[type="radio"].custom-pgs-selector:checked, .grid-variants input[type="radio"].custom-pgs-selector:checked'
    );

    let addonPrice = 0;
    addonRadios.forEach((radio) => {
      addonPrice += parseInt(radio.dataset.price, 10) || 0;
    });

    const totalPrice = basePrice + addonPrice;
    const currencyFormat = window.themeVariables.settings.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;
    priceDisplay.innerHTML = formatMoney(totalPrice, currencyFormat);
  };

  popup.addEventListener("change", function (e) {
    if (
      e.target.matches(
        '.grid-selector input[type="radio"].custom-pgs-selector, .grid-variants input[type="radio"].custom-pgs-selector'
      )
    ) {
      updatePopupPrice();
    }
  });

  // Select first radio by default
  /*
  const firstRadio = popup.querySelector(
    '.grid-selector input[type="radio"].custom-pgs-selector, .grid-variants input[type="radio"].custom-pgs-selector'
  );
  if (
    firstRadio &&
    !popup.querySelector(
      '.grid-selector input[type="radio"].custom-pgs-selector:checked, .grid-variants input[type="radio"].custom-pgs-selector:checked'
    )
  ) {
    firstRadio.checked = true;
  }*/

  updatePopupPrice();
});

// Auto-proceed to next tab when selection is made
document.addEventListener("DOMContentLoaded", function () {
  const popup = document.querySelector("#product-customizer");
  if (!popup) return;

  // Track auto-navigation state for each tab
  const autoNavigationState = {
    "tab-reglazing": false,
    "tab-lens-type": false,
    "tab-prescription": false,
    "tab-thinning": false,
    "tab-coating": false,
    "tab-tinting": false,
  };

  // Tab configuration for input selectors
  const tabConfig = {
    "tab-reglazing": {
      radioSelector: 'input[name="properties[Reglazing]"]',
      gridSelector: ".grid-selector-item",
    },
    "tab-lens-type": {
      radioSelector: 'input[name="properties[Lens Type]"]',
      gridSelector: ".grid-selector-item",
    },
    "tab-prescription": {
      radioSelector: 'input[name="properties[Prescription]"]',
      gridSelector: ".grid-selector-item",
    },
    "tab-thinning": {
      radioSelector: ".addon-selector",
      gridSelector: ".grid-variants-item",
    },
    "tab-coating": {
      radioSelector: ".addon-selector",
      gridSelector: ".grid-variants-item",
    },
    "tab-tinting": {
      radioSelector: ".addon-selector",
      gridSelector: ".grid-variants-item",
    },
  };

  // Function to navigate to next tab
  const navigateToNextTab = (currentTabTarget) => {
    // Find the active tab button
    const activeTabButton = popup.querySelector(
      `.popup__product-tabs .tab-item[data-target="${currentTabTarget}"]`
    );
    if (!activeTabButton) return;

    // Find the next tab button
    const nextTabButton = activeTabButton.nextElementSibling;
    if (!nextTabButton || !nextTabButton.dataset.target) return;

    const nextTabTarget = nextTabButton.dataset.target;

    // Trigger click on next tab button to navigate
    nextTabButton.click();
  };

  // Function to set up auto-navigation for a specific tab
  const setupAutoNavigation = (tabTarget) => {
    const config = tabConfig[tabTarget];
    if (!config) return;

    const tabElement = popup.querySelector(
      `.popup__product-content [data-target="${tabTarget}"]`
    );
    if (!tabElement) return;

    // Listen for radio button changes
    const radios = tabElement.querySelectorAll(config.radioSelector);
    const tabActions = tabElement.parentElement.querySelector(".tab-actions");

    if (tabActions) {
      tabActions.setAttribute("style", "display: none");
    }

    radios.forEach((radio) => {
      radio.addEventListener("change", function (e) {
        console.log(
          "change",
          e.currentTarget.value,
          this.checked,
          autoNavigationState[tabTarget]
        );

        if (
          tabTarget == "tab-prescription" &&
          e.currentTarget.value != "Send later"
        ) {
          tabActions.setAttribute("style", "display: flex");
          return;
        } else if (
          tabTarget == "tab-prescription" &&
          e.currentTarget.value == "Send later"
        ) {
          tabActions.setAttribute("style", "display: none");
          autoNavigationState[tabTarget] = false;
        }

        if (tabTarget == "tab-tinting" && e.currentTarget.value != "None") {
          tabActions.setAttribute("style", "display: flex");
          console.log("tab-tinting: option selected");

          if (window.innerWidth > 999) {
            document.querySelector(".popup .modal__content").scrollTo({
              top: 0,
            });

            setTimeout(() => {
              let y = tabActions.getBoundingClientRect().bottom;
              y = y - y * 0.35;

              document.querySelector(".popup .modal__content").scrollTo({
                top: y,
                behavior: "smooth",
              });
            }, 10);
          }

          return;
        } else if (
          tabTarget == "tab-tinting" &&
          e.currentTarget.value == "None"
        ) {
          tabActions.setAttribute("style", "display: none");

          // Disable tinting sub-option inputs (color/percentage) so they
          // don't block validation when "None" / Clear Lenses is selected.
          // The popup-options div is a sibling of tabElement, inside the
          // same .shopify-section--popup-tabs parent section.
          const tintingSection = tabElement.closest(
            ".shopify-section--popup-tabs"
          );
          if (tintingSection) {
            const contentTabs = tintingSection.querySelectorAll(
              ".popup-options__content-tab"
            );
            contentTabs.forEach((tab) => {
              tab.setAttribute("hidden", "");
              const inputs = tab.querySelectorAll(
                ".input__field, .select__field:not(.no-update), .radio__field"
              );
              inputs.forEach((input) => input.setAttribute("disabled", ""));
            });
          }

          autoNavigationState[tabTarget] = true;

          setTimeout(() => {
            navigateToNextTab(tabTarget);
          }, 300);
          return;
        }

        // Only auto-navigate once per tab session and only when a selection is made
        if (!autoNavigationState[tabTarget] && this.checked) {
          autoNavigationState[tabTarget] = true;

          // Small delay to ensure selection is fully processed
          setTimeout(() => {
            navigateToNextTab(tabTarget);
          }, 300);
        }
      });
    });

    // Handle click events for grid selector items
    /*
    const gridItems = tabElement.querySelectorAll(config.gridSelector);
    gridItems.forEach((item) => {
      item.addEventListener("click", function (e) {
        const radio = this.querySelector(config.radioSelector);

        if (
          tabTarget == "tab-prescription" &&
          e.currentTarget.dataset.option != "send-later"
        ) {
          console.log("show continue button");
          return;
        }

        if (radio && radio.checked && !autoNavigationState[tabTarget]) {
          autoNavigationState[tabTarget] = true;

          setTimeout(() => {
            navigateToNextTab(tabTarget);
          }, 300);
        }
      });
    });
    */
  };

  // Set up auto-navigation for all configured tabs
  Object.keys(tabConfig).forEach((tabTarget) => {
    setupAutoNavigation(tabTarget);
  });

  // Handle tinting mobile option clicks directly
  // The theme JS clicks the hidden grid item (.hidden-phone), but the label's
  // default behavior (checking the radio) doesn't fire reliably on display:none
  // elements. This explicitly checks the radio and dispatches the change event.
  const tintingMobileOptions = popup.querySelectorAll(
    ".tab-tinting .popup-option-mobile"
  );
  tintingMobileOptions.forEach((option) => {
    option.addEventListener("click", function () {
      const value = this.dataset.value;
      const tintingSection = popup.querySelector(
        '.popup__product-content [data-target="tab-tinting"]'
      );
      if (!tintingSection) return;
      const radio = tintingSection.querySelector(
        `.addon-selector[value="${value}"]`
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event("change"));
      }
    });
  });

  // Reset auto-navigation flag when returning to any tab
  const tabButtons = popup.querySelectorAll(".popup__product-tabs .tab-item");
  tabButtons.forEach((button) => {
    button.addEventListener("click", function () {
      const tabTarget = this.dataset.target;
      if (tabTarget && autoNavigationState.hasOwnProperty(tabTarget)) {
        autoNavigationState[tabTarget] = false;
      }
    });
  });
});

// Popup review tab - Display selected options
document.addEventListener("DOMContentLoaded", function () {
  const updateReviewDisplay = () => {
    const reviewSummary = document.querySelector(".review-summary");
    if (!reviewSummary) {
      console.log("Review summary not found");
      return;
    }

    console.log("Updating review display");

    const productVendor =
      document.querySelector(".product-vendor")?.textContent.trim() || "";
    const productTitle =
      document.querySelector(".product-title")?.textContent.trim() || "";
    const selectedVariant =
      document.querySelector(".product-variant")?.textContent.trim() || "";
    const basePrice =
      document.querySelector(".popup__total")?.dataset.main_price || "";
    const productPrice = basePrice
      ? formatMoney(
          parseInt(basePrice),
          window.themeVariables.settings.moneyFormat
        )
      : "";

    const totalPriceElement = document.querySelector(".popup__total .price");
    const totalPriceText = totalPriceElement?.textContent.trim() || "";
    const priceDigits = totalPriceText.replace(/[^0-9.]/g, "");
    let totalPriceInCents = priceDigits.includes(".")
      ? parseInt(parseFloat(priceDigits) * 100)
      : parseInt(priceDigits) * 100;
    const basePriceInCents = basePrice ? parseInt(basePrice) : 0;
    const lensPriceInCents = totalPriceInCents - basePriceInCents;
    const lensPrice =
      lensPriceInCents > 0
        ? formatMoney(
            lensPriceInCents,
            window.themeVariables.settings.moneyFormat
          )
        : "";

    console.log("Price calculation:", {
      totalPriceText,
      priceDigits,
      totalPriceInCents,
      basePriceInCents,
      lensPriceInCents,
      lensPrice,
    });

    setTimeout(() => {
      const tabReglazingElement = document.querySelector(
        `.popup__product-content [data-target="tab-reglazing"]`
      );

      const selectedReglazingOption = tabReglazingElement.querySelector(
        '[name="properties[Reglazing]"]:checked'
      );

      const reglazingPriceInCents = Number(
        selectedReglazingOption.dataset.price
      );
      const totalPriceInCentsTemp = totalPriceInCents + reglazingPriceInCents;
      const lensPriceInCentsTemp = lensPriceInCents + reglazingPriceInCents;

      const total = document.querySelector(".popup__total");
      const priceDisplay = total?.querySelector("span.price");

      const currencyFormat = window.themeVariables.settings.currencyCodeEnabled
        ? window.themeVariables.settings.moneyWithCurrencyFormat
        : window.themeVariables.settings.moneyFormat;

      priceDisplay.innerHTML = formatMoney(
        totalPriceInCentsTemp,
        currencyFormat
      );

      const lensPriceHolder = document.querySelector(".lens-price-holder");
      lensPriceHolder.innerHTML = formatMoney(
        lensPriceInCentsTemp,
        currencyFormat
      );
    }, 200);

    const lensOptions = [];
    const allTabs = document.querySelectorAll(".wrapper[data-name]");
    allTabs.forEach((tab) => {
      const tabName = tab.dataset.name;
      if (tabName && tabName !== "Review") {
        const selectedRadio = tab.querySelector(
          'input[type="radio"].custom-pgs-selector:checked, input[type="radio"].addon-selector:checked'
        );
        if (selectedRadio) {
          let optionTitle = selectedRadio.value;
          if (tabName === "Tinting" && optionTitle === "None") {
            optionTitle = "Clear Lenses";
          }
          lensOptions.push({ key: tabName, value: optionTitle });
        }
      }
    });

    const tintingIndex = lensOptions.findIndex((opt) => opt.key === "Tinting");
    if (
      tintingIndex !== -1 &&
      lensOptions[tintingIndex].value !== "Clear Lenses"
    ) {
      const tintingDetails = [];
      const tintColorInputs = document.querySelectorAll(
        'input[name*="Color"]:checked'
      );
      tintColorInputs.forEach((input) => {
        if (input.value) {
          const label = input.name.replace("properties[", "").replace("]", "");
          tintingDetails.push(`${label}: ${input.value}`);
        }
      });

      const tintPercentageInputs = document.querySelectorAll(
        'input[name*="Darkness Percentage"]:checked'
      );
      tintPercentageInputs.forEach((input) => {
        if (input.value) {
          tintingDetails.push(`Darkness: ${input.value}`);
        }
      });

      if (tintingDetails.length > 0) {
        lensOptions[tintingIndex].details = tintingDetails;
      }
    }

    const prescriptionIndex = lensOptions.findIndex(
      (opt) => opt.key === "Prescription"
    );
    if (prescriptionIndex !== -1) {
      const activeOption = document.querySelector(
        ".popup-options__content-tab:not([hidden])"
      );

      if (activeOption) {
        const prescriptionDetails = [];

        const odRow = activeOption.querySelector('[data-eye="OD (Right)"]');
        if (odRow) {
          const sph = odRow.querySelector('select[data-title="SPH"]')?.value;
          const cyl = odRow.querySelector('select[data-title="CYL"]')?.value;
          const axis = odRow.querySelector('select[data-title="AXIS"]')?.value;
          const add = odRow.querySelector('select[data-title="ADD"]')?.value;
          if (sph) {
            let odValue = `SPH: ${sph}`;
            if (cyl) odValue += `, CYL: ${cyl}`;
            if (axis) odValue += `, AXIS: ${axis}`;
            if (add) odValue += `, ADD: ${add}`;
            prescriptionDetails.push(`OD (Right): ${odValue}`);
          }
        }

        const osRow = activeOption.querySelector('[data-eye="OS (Left)"]');
        if (osRow) {
          const sph = osRow.querySelector('select[data-title="SPH"]')?.value;
          const cyl = osRow.querySelector('select[data-title="CYL"]')?.value;
          const axis = osRow.querySelector('select[data-title="AXIS"]')?.value;
          const add = osRow.querySelector('select[data-title="ADD"]')?.value;
          if (sph) {
            let osValue = `SPH: ${sph}`;
            if (cyl) osValue += `, CYL: ${cyl}`;
            if (axis) osValue += `, AXIS: ${axis}`;
            if (add) osValue += `, ADD: ${add}`;
            prescriptionDetails.push(`OS (Left): ${osValue}`);
          }
        }

        const singlePD = activeOption.querySelector(
          'select[name="properties[Single PD]"]'
        );
        if (
          singlePD &&
          singlePD.value &&
          !singlePD.closest(".pd-container").hidden
        ) {
          prescriptionDetails.push(`Single PD: ${singlePD.value}`);
        }

        const leftPD = activeOption.querySelector(
          'select[name="properties[Left Eye PD]"]'
        );
        const rightPD = activeOption.querySelector(
          'select[name="properties[Right Eye PD]"]'
        );
        if (leftPD && leftPD.value && !leftPD.closest(".pd-container").hidden) {
          prescriptionDetails.push(`Left Eye PD: ${leftPD.value}`);
        }
        if (
          rightPD &&
          rightPD.value &&
          !rightPD.closest(".pd-container").hidden
        ) {
          prescriptionDetails.push(`Right Eye PD: ${rightPD.value}`);
        }

        const optometristName = activeOption.querySelector(
          'input[name="properties[Optometrist Name]"]'
        );
        const optometristNumber = activeOption.querySelector(
          'input[name="properties[Optometrist Number]"]'
        );
        const optometristSuburb = activeOption.querySelector(
          'input[name="properties[Optometrist Suburb]"]'
        );
        if (optometristName && optometristName.value) {
          prescriptionDetails.push(
            `Optometrist Name: ${optometristName.value}`
          );
        }
        if (optometristNumber && optometristNumber.value) {
          prescriptionDetails.push(
            `Optometrist Number: ${optometristNumber.value}`
          );
        }
        if (optometristSuburb && optometristSuburb.value) {
          prescriptionDetails.push(
            `Optometrist Suburb: ${optometristSuburb.value}`
          );
        }

        const prescriptionURL = activeOption.querySelector(
          'input[name="properties[Prescription URL]"]'
        );
        if (prescriptionURL && prescriptionURL.value) {
          prescriptionDetails.push(
            `Prescription file: ${prescriptionURL.value}`
          );
        }

        if (prescriptionDetails.length > 0) {
          lensOptions[prescriptionIndex].details = prescriptionDetails;
        }
      }
    }

    console.log(
      "Product:",
      productTitle,
      "Variant:",
      selectedVariant,
      "Options:",
      lensOptions
    );

    let reviewHTML = "";

    reviewHTML += '<div class="review-section" style="margin-bottom: 1.5rem;">';
    reviewHTML +=
      '<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">';
    reviewHTML +=
      '<h3 style="font-weight: bold; margin: 0;font-size: 20px;">Lenses</h3>';
    if (productPrice)
      reviewHTML += `<span style="font-weight: 400;font-size: 20px;">${productPrice}</span>`;
    reviewHTML += "</div>";
    if (productVendor && productTitle) {
      reviewHTML += `<p style="margin: 0.25rem 0; ">${productVendor} ${productTitle}</p>`;
    } else if (productVendor) {
      reviewHTML += `<p style="margin: 0.25rem 0;">${productVendor}</p>`;
    } else if (productTitle) {
      reviewHTML += `<p style="margin: 0.25rem 0;">${productTitle}</p>`;
    }
    if (selectedVariant && selectedVariant != "Default Title")
      reviewHTML += `<p style="margin: 0.25rem 0;">${selectedVariant}</p>`;
    reviewHTML += "</div>";

    if (lensOptions.length > 0) {
      reviewHTML += '<div class="review-section">';
      reviewHTML +=
        '<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">';
      reviewHTML +=
        '<h3 style="font-weight: bold; margin: 0; font-size: 20px;">Additional Features</h3>';
      if (lensPrice)
        reviewHTML += `<span style="font-weight: 400;font-size: 20px;">${lensPrice}</span>`;
      reviewHTML += "</div>";
      lensOptions.forEach((option) => {
        reviewHTML += `<p style="margin: 0.8rem 0 0 0 !important; font-weight: 700 !important;">${option.key}:</p>`;
        reviewHTML += `<p style="margin: 0.25rem 0;">${option.value}</p>`;
        if (option.details && option.details.length > 0) {
          option.details.forEach((detail) => {
            reviewHTML += `<p style="margin: 0.25rem 0 0 1rem; font-size: 0.9em;">${detail}</p>`;
          });
        }
      });
      reviewHTML += "</div>";
    }

    console.log("Review HTML:", reviewHTML);
    reviewSummary.innerHTML = reviewHTML;
    console.log("Review summary element after update:", reviewSummary);
  };

  document.addEventListener("click", (e) => {
    if (e.target.closest('[data-target="tab-review"]')) {
      setTimeout(updateReviewDisplay, 200);
    }
  });

  document.querySelector(".to-review-tab").addEventListener("click", (e) => {
    setTimeout(updateReviewDisplay, 200);
  });

  const observer = new MutationObserver(() => {
    const reviewTab = document.querySelector(
      '[data-target="tab-review"].is-selected'
    );
    if (reviewTab) {
      updateReviewDisplay();
    }
  });

  observer.observe(document.body, {
    attributes: true,
    subtree: true,
    attributeFilter: ["class"],
  });
});

// Popup review tab - Add to cart button handler
document.addEventListener("DOMContentLoaded", function () {
  const reviewSubmitBtn = document.querySelector(
    '[data-submit-btn="popup-form-add-to-cart"]'
  );
  if (!reviewSubmitBtn) return;

  reviewSubmitBtn.addEventListener("click", function () {
    const addToCartBtn = document.querySelector(".popup-form-add-to-cart");
    if (!addToCartBtn) return;

    reviewSubmitBtn.style.display = "inline-flex";
    reviewSubmitBtn.style.alignItems = "center";
    reviewSubmitBtn.style.gap = "8px";

    const loader = document.createElement("span");
    loader.style.display = "inline-flex";
    loader.innerHTML = `
      <div class="spinner">
        <svg focusable="false" width="20" height="20" class="icon icon--spinner" viewBox="25 25 50 50">
          <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
        </svg>
      </div>
    `;
    reviewSubmitBtn.prepend(loader);
    reviewSubmitBtn.disabled = true;

    addToCartBtn.click();

    setTimeout(() => {
      loader.remove();
      reviewSubmitBtn.disabled = false;
    }, 2000);
  });
});
